import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  ClipboardCheck,
  Users,
  LogOut,
  Menu,
  X,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import NotificationsBell from "@/components/NotificationsBell";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, roles: ["Admin", "Manager", "Employee"], testid: "nav-dashboard" },
  { to: "/tasks", label: "Tasks", icon: ClipboardCheck, roles: ["Admin", "Manager", "Employee"], testid: "nav-tasks" },
  { to: "/team", label: "Team", icon: Users, roles: ["Admin", "Manager"], testid: "nav-team" },
];

function roleBadge(role) {
  const map = {
    Admin: "bg-slate-900 text-white",
    Manager: "bg-indigo-600 text-white",
    Employee: "bg-emerald-600 text-white",
  };
  return map[role] || "bg-slate-500 text-white";
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const nav = NAV.filter((n) => n.roles.includes(user.role));

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky lg:top-0 inset-y-0 left-0 z-40 w-64 h-screen bg-white border-r border-slate-200 flex flex-col transform transition-transform lg:transform-none ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-16 flex items-center gap-3 px-5 border-b border-slate-200 shrink-0">
          <div className="h-9 w-9 bg-slate-900 flex items-center justify-center">
            <Wrench className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="label-eyebrow">KGOF</div>
            <div className="font-black text-slate-900 -mt-0.5">To Do</div>
          </div>
        </div>

        <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-200 bg-white shrink-0">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 bg-slate-100 border border-slate-200 rounded-full flex items-center justify-center text-sm font-bold text-slate-700">
              {user.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-900 truncate" data-testid="sidebar-user-name">{user.name}</div>
              <div className="mt-0.5 flex items-center gap-1">
                <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-bold ${roleBadge(user.role)}`}>
                  {user.role}
                </span>
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-slate-600 hover:text-rose-600 hover:bg-rose-50 mt-1"
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 bg-slate-900/40 z-30 lg:hidden"
        />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-20 backdrop-blur-xl bg-white/80 border-b border-slate-200 flex items-center px-4 lg:px-8 gap-3">
          <button
            onClick={() => setMobileOpen((s) => !s)}
            className="lg:hidden h-9 w-9 flex items-center justify-center border border-slate-200 rounded-md"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
          <div className="label-eyebrow hidden sm:block">Ops Console</div>
          <div className="flex-1" />
          <div className="text-xs text-slate-500 hidden sm:block">
            {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </div>
          <NotificationsBell />
        </header>

        <main className="flex-1 p-4 lg:p-8 max-w-[1600px] w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
